import type { UnistylesBreakpoints } from "react-native-unistyles";
import { UnistylesRuntime } from "react-native-unistyles";

import CustomPlatform from "$src/core/utils/platform/platform";

const scaled = (value: number) =>
  CustomPlatform.select({
    tv: Math.round(value * (UnistylesRuntime.screen.width / 1200)),
    default: value,
  });

const FONT_SIZES = {
  xxs: 10,
  xs: 12,
  s: 14,
  m: 16,
  l: 18,
  xl: 20,
  xxl: 22,
  xxxl: 26,
  xxxxl: 32,
} as const satisfies Record<string, number>;

const LINE_HEIGHTS = {
  xxs: 14,
  xs: 16,
  s: 20,
  m: 22,
  l: 24,
  xl: 26,
  xxl: 28,
  xxxl: 32,
  xxxxl: 38,
} as const satisfies Record<string, number>;

const theme = {
  breakpoints: {
    xs: 0,
    sm: 576,
    md: 768,
    // TODO FIND A BETTER WAY TO HANDLE THIS
    lg: CustomPlatform.select({ androidTV: 769, default: 1024 }),
    xl: 1200,
    xxl: 1600,
    xxxl: 1900,
  },
  border: {
    widths: {
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    },
    radius: {
      xs: 1,
      sm: 2,
      md: 4,
      lg: 8,
      full: 9999,
    },
  },
  font: {
    families: {
      heading1: "System",
      heading2: "System",
      heading3: "System",
      heading4: "System",
      heading5: "System",
      heading6: "System",
      heading7: "System",
      body: "System",
      button: "System",
      sub: "System",
    } satisfies Record<string, string>,
    sizes: CustomPlatform.select({
      tv: {
        heading1: {
          xs: scaled(FONT_SIZES.xxxxl),
        },
        heading2: {
          xs: scaled(FONT_SIZES.xxxl),
        },
        heading3: {
          xs: scaled(FONT_SIZES.xxl),
        },
        heading4: {
          xs: scaled(FONT_SIZES.xl),
        },
        heading5: {
          xs: scaled(FONT_SIZES.l),
        },
        heading6: {
          xs: scaled(FONT_SIZES.m),
        },
        heading7: {
          xs: scaled(FONT_SIZES.m),
        },
        body: {
          xs: scaled(FONT_SIZES.m),
        },
        sub: {
          xs: scaled(FONT_SIZES.s),
        },
        caption: {
          xs: scaled(FONT_SIZES.xs),
        },
      },
      default: {
        heading1: {
          xs: FONT_SIZES.xxxl,
          lg: FONT_SIZES.xxxxl,
        },
        heading2: {
          xs: FONT_SIZES.xxl,
          lg: FONT_SIZES.xxxl,
        },
        heading3: {
          xs: FONT_SIZES.xl,
          lg: FONT_SIZES.xxl,
        },
        heading4: {
          xs: FONT_SIZES.l,
          lg: FONT_SIZES.xl,
        },
        heading5: {
          xs: FONT_SIZES.l,
          lg: FONT_SIZES.l,
        },
        heading6: {
          xs: FONT_SIZES.m,
          lg: FONT_SIZES.m,
        },
        heading7: {
          xs: FONT_SIZES.m,
          lg: FONT_SIZES.m,
        },
        body: {
          xs: FONT_SIZES.m,
          lg: FONT_SIZES.m,
        },
        sub: {
          xs: FONT_SIZES.s,
          lg: FONT_SIZES.s,
        },
        caption: {
          xs: FONT_SIZES.xs,
          lg: FONT_SIZES.xs,
        },
      } satisfies Record<
        string,
        Partial<Record<keyof UnistylesBreakpoints, number>>
      >,
    }),
    lineHeights: CustomPlatform.select({
      tv: {
        heading1: {
          xs: scaled(LINE_HEIGHTS.xxxxl),
        },
        heading2: {
          xs: scaled(LINE_HEIGHTS.xxxl),
        },
        heading3: {
          xs: scaled(LINE_HEIGHTS.xxl),
        },
        heading4: {
          xs: scaled(LINE_HEIGHTS.xl),
        },
        heading5: {
          xs: scaled(LINE_HEIGHTS.l),
        },
        heading6: {
          xs: scaled(LINE_HEIGHTS.m),
        },
        heading7: {
          xs: scaled(LINE_HEIGHTS.m),
        },
        body: {
          xs: scaled(LINE_HEIGHTS.l),
        },
        sub: {
          xs: scaled(LINE_HEIGHTS.s),
        },
        caption: {
          xs: scaled(LINE_HEIGHTS.xxs),
        },
      },
      default: {
        heading1: {
          xs: LINE_HEIGHTS.xxxl,
          lg: LINE_HEIGHTS.xxxxl,
        },
        heading2: {
          xs: LINE_HEIGHTS.xxl,
          lg: LINE_HEIGHTS.xxxl,
        },
        heading3: {
          xs: LINE_HEIGHTS.xl,
          lg: LINE_HEIGHTS.xxl,
        },
        heading4: {
          xs: LINE_HEIGHTS.l,
          lg: LINE_HEIGHTS.xl,
        },
        heading5: {
          xs: LINE_HEIGHTS.m,
          lg: LINE_HEIGHTS.l,
        },
        heading6: {
          xs: LINE_HEIGHTS.s,
          lg: LINE_HEIGHTS.m,
        },
        heading7: {
          xs: LINE_HEIGHTS.s,
          lg: LINE_HEIGHTS.m,
        },
        body: {
          xs: LINE_HEIGHTS.l,
          lg: LINE_HEIGHTS.l,
        },
        sub: {
          xs: LINE_HEIGHTS.s,
          lg: LINE_HEIGHTS.s,
        },
        caption: {
          xs: LINE_HEIGHTS.xxs,
          lg: LINE_HEIGHTS.xxs,
        },
      } satisfies Record<
        string,
        Partial<Record<keyof UnistylesBreakpoints, number>>
      >,
    }),
  },
  colors: {
    backgroundDefault: "#000000",
    backgroundSurface: "#000000",
    backgroundFloat: "#000000",
    backgroundActive: "#000000",
    backgroundHover: "#000000",
    backgroundFocus: "#000000",
    backgroundInverted: "#ffffff",
    backgroundPositive: "#22c55e",
    backgroundNegative: "#ef4444",
    backgroundBrand: "#000000",
    backgroundBackdrop: "rgba(0, 0, 0, 0.9)",
    backgroundScrim: "#000000",

    foregroundDefault: "#ffffff",
    foregroundDefaultMuted: "#ffffff",
    foregroundBrand: "#ffffff",
    foregroundOnSurface: "#ffffff",
    foregroundOnFloat: "#ffffff",
    foregroundOnActive: "#ffffff",
    foregroundOnFocus: "#ffffff",
    foregroundOnHover: "#ffffff",
    foregroundOnInverted: "#ffffff",
    foregroundOnPositive: "#ffffff",
    foregroundOnNegative: "#ffffff",
    foregroundOnBrand: "#ffffff",

    borderDefault: "#ffffff",
    borderFocus: "#000000",
    borderPositive: "#22c55e",
    borderNegative: "#ef4444",
    borderBrand: "#ffffff",
  } satisfies Record<string, string>,
  shadows: {
    tabBar: "#000000",
    modal: "#000000",
  } satisfies Record<string, string>,
  profile: {
    tints: ["#4E8FED", "#FF4E68", "#FDA80F", "#0D9668"],
  },
  spacing: (value: number) => scaled(value * 4),
} as const;

export default theme;
